import { DateTime } from "luxon";
import { useCallback, useEffect, useState } from "react";
import { ViewProps } from "./calendar/capture-calendar-types";
import {
  Cell,
  CellUnit,
  RangeEndpoint,
  extendRange,
  isPickable,
  pickDate,
  resolveAnchor,
} from "./range-selection";

type RangeInteraction = {
  /** The endpoint the next click moves, chosen in the heading */
  editing?: RangeEndpoint;
  /** Choose the endpoint the next click moves, or none for plain picks */
  setEditing(endpoint?: RangeEndpoint): void;
  /** The range to draw: the active one, or the one a drag or click would make */
  shown: [start?: Date, end?: Date];
  /** The interaction props of CaptureCalendar */
  calendarProps: Pick<
    ViewProps,
    "onPick" | "onHover" | "onPress" | "isDragging" | "isPickable"
  > & { isEditing: boolean };
  /** Forget the edited endpoint and the anchor, as when the range is cleared */
  reset(): void;
};

/**
 * Clicks, shift-clicks, drags and endpoint edits on the capture calendar, all reduced to a
 * new start and end date.
 *
 * @returns The interaction state and the props wiring it into CaptureCalendar
 * @param startDate Inclusive start of the active range
 * @param endDate Inclusive end of the active range
 * @param setRange Apply a new range
 */
export function useRangeInteraction(
  startDate: Date | undefined,
  endDate: Date | undefined,
  setRange: (start: Date, end: Date) => void,
): RangeInteraction {
  const [editing, setEditing] = useState<RangeEndpoint>();
  // The last cell picked alone, kept selected while shift-clicks move the other end
  const [anchor, setAnchor] = useState<[start: Date, end: Date]>();
  const [hovered, setHovered] = useState<Cell>();
  // The cell a drag started on; the drag spans it and the hovered cell
  const [dragOrigin, setDragOrigin] = useState<Cell>();

  // While dragging, the last cell is kept as the pointer crosses the gaps between cells
  const onHover = useCallback(
    (cell?: Cell) =>
      setHovered((current) => cell ?? (dragOrigin ? current : undefined)),
    [dragOrigin],
  );

  const dragRange =
    dragOrigin &&
    hovered &&
    !(hovered[1] === dragOrigin[1] && hovered[0].hasSame(...dragOrigin))
      ? extendRange(...hovered, pickDate(...dragOrigin))
      : undefined;

  useEffect(() => {
    if (!dragOrigin) return;
    function endDrag(event: PointerEvent): void {
      setDragOrigin(undefined);
      // Released on the pressed cell, the drag was a click, which onPick handles
      if (event.type === "pointercancel" || !dragOrigin || !dragRange) return;
      setRange(...dragRange);
      setAnchor(pickDate(...dragOrigin));
      setEditing(undefined);
    }
    // Capture phase, as EventBarrier stops the release from bubbling up to the window
    const options = { capture: true };
    window.addEventListener("pointerup", endDrag, options);
    // A touch the browser takes over, e.g. to scroll the list, never gets a pointerup
    window.addEventListener("pointercancel", endDrag, options);
    return () => {
      window.removeEventListener("pointerup", endDrag, options);
      window.removeEventListener("pointercancel", endDrag, options);
    };
  }, [dragOrigin, dragRange, setRange]);

  const onPick = useCallback(
    (date: DateTime, unit: CellUnit, isShiftHeld: boolean) => {
      const base = resolveAnchor(anchor, startDate, endDate);
      if (isShiftHeld && base) {
        setRange(...extendRange(date, unit, base));
        setAnchor(base);
        setEditing(undefined);
        return;
      }
      const range = pickDate(date, unit, editing, startDate, endDate);
      setRange(...range);
      setEditing(undefined);
      if (!editing) setAnchor(range);
    },
    [anchor, editing, startDate, endDate, setRange],
  );

  const isCellPickable = useCallback(
    (date: DateTime, unit: CellUnit) =>
      isPickable(date, unit, editing, startDate, endDate),
    [editing, startDate, endDate],
  );

  const reset = useCallback(() => {
    setEditing(undefined);
    setAnchor(undefined);
  }, []);

  return {
    editing,
    setEditing,
    shown:
      dragRange ??
      (editing && hovered
        ? pickDate(...hovered, editing, startDate, endDate)
        : [startDate, endDate]),
    calendarProps: {
      onPick,
      onHover,
      onPress: setDragOrigin,
      isDragging: !!dragOrigin,
      isEditing: !!editing,
      isPickable: isCellPickable,
    },
    reset,
  };
}
